
"use client";

import { useState, useEffect } from 'react';
import Navigation from '@/components/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { tokenStore } from '@/lib/api/client';
import {
  User,
  Store,
  Settings,
  Package,
  Heart,
  CreditCard,
  LogOut,
  Briefcase,
  History,
  ShieldCheck,
  BellRing,
  PlusCircle,
  Wrench,
  ClipboardList,
  MessageCircle,
  Tag,
  Hash,
  ChevronLeft,
  Banknote,
  Receipt,
  FileText,
  Sparkles,
  LogIn,
  Car,
  Search,
  ArrowLeft,
  Star,
  Zap,
  UserPlus,
  Lock,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import Link from 'next/link';
import { PART_CATEGORIES } from '@/app/lib/mock-db';
import { useRouter } from 'next/navigation';
import {
  clearStoredSession,
  getStoredAccessToken,
  normalizeRole,
  notifySessionChanged,
  readStoredUser,
} from '@/lib/auth/session';

const MOCK_VENDOR_ORDERS = [
  { id: 'ORD-7721', customer: 'أحمد صالح', item: 'فحمات فرامل أمامية', price: 250, status: 'pending', date: '2024-05-20', chatId: 'chat_1' },
  { id: 'ORD-8812', customer: 'سارة محمد', item: 'تغيير زيت وفلتر', price: 180, status: 'completed', date: '2024-05-18', chatId: 'chat_3' },
];

// ─── Guest Landing View ────────────────────────────────────────────────────────

function GuestProfileView() {
  const features = [
    {
      icon: Car,
      label: 'سوق السيارات',
      desc: 'تصفح آلاف الإعلانات وأضف سيارتك للبيع',
      color: 'text-blue-600',
      bg: 'bg-blue-50',
    },
    {
      icon: Wrench,
      label: 'الصيانة والورش',
      desc: 'احجز وتتبع طلبات الصيانة في الورش المعتمدة',
      color: 'text-purple-600',
      bg: 'bg-purple-50',
    },
    {
      icon: Package,
      label: 'قطع الغيار',
      desc: 'تسوق قطع الغيار الأصلية والتجارية بأفضل الأسعار',
      color: 'text-orange-500',
      bg: 'bg-orange-50',
    },
    {
      icon: Sparkles,
      label: 'الفحص الذكي',
      desc: 'فحص سيارتك بالذكاء الاصطناعي ومعرفة مشاكلها',
      color: 'text-emerald-600',
      bg: 'bg-emerald-50',
    },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navigation title="الملف الشخصي" />

      <div className="p-4 max-w-2xl mx-auto space-y-6 pb-24">

        {/* Hero Guest Card */}
        <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 rounded-[2.5rem] p-8 shadow-xl shadow-blue-200 relative overflow-hidden text-center">
          {/* decorative blobs */}
          <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -mr-24 -mt-24 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full -ml-16 -mb-16 pointer-events-none" />

          <div className="relative z-10">
            {/* Avatar placeholder */}
            <div className="h-24 w-24 rounded-full bg-white/10 border-4 border-white/20 flex items-center justify-center mx-auto mb-5 shadow-xl">
              <User className="h-10 w-10 text-white/70" />
            </div>

            <h2 className="text-2xl font-extrabold text-white mb-2">مرحباً بك في مكنة</h2>
            <p className="text-blue-200 text-sm mb-6 leading-relaxed">
              سجّل دخولك للوصول إلى ملفك الشخصي، إعلاناتك، طلبات الصيانة، والمزيد.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link href="/login" className="flex-1 sm:flex-none">
                <Button className="w-full sm:w-auto rounded-2xl bg-white text-blue-700 hover:bg-blue-50 font-extrabold shadow-lg h-12 px-8 gap-2 transition-all hover:scale-[1.02]">
                  <LogIn className="h-5 w-5" />
                  تسجيل الدخول
                </Button>
              </Link>
              <Link href="/public-register" className="flex-1 sm:flex-none">
                <Button variant="outline" className="w-full sm:w-auto rounded-2xl border-white/30 text-white hover:bg-white/10 bg-white/5 font-extrabold h-12 px-8 gap-2 transition-all hover:scale-[1.02]">
                  <UserPlus className="h-5 w-5" />
                  إنشاء حساب مجاني
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* What you get when you sign in */}
        <div className="space-y-3">
          <p className="text-xs font-extrabold text-slate-400 uppercase tracking-widest px-1">
            ما الذي ستحصل عليه بعد التسجيل؟
          </p>
          <div className="grid grid-cols-2 gap-3">
            {features.map((f) => (
              <div
                key={f.label}
                className="bg-white rounded-[1.75rem] p-5 shadow-sm border border-slate-50 flex flex-col gap-3 hover:shadow-md transition-shadow"
              >
                <div className={`p-3 ${f.bg} ${f.color} rounded-2xl w-fit`}>
                  <f.icon className="h-6 w-6" />
                </div>
                <div>
                  <p className="font-extrabold text-slate-800 text-sm">{f.label}</p>
                  <p className="text-slate-400 text-[11px] mt-0.5 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits list */}
        <div className="bg-white rounded-[2.5rem] p-6 shadow-sm space-y-4">
          <h3 className="font-extrabold text-slate-800 flex items-center gap-2 text-base">
            <Star className="h-5 w-5 text-yellow-500" />
            مزايا الحساب المجاني
          </h3>
          {[
            { icon: Tag, label: 'أضف إعلانات بيع السيارات مجاناً', color: 'text-emerald-600', bg: 'bg-emerald-50' },
            { icon: Wrench, label: 'تتبع طلبات الصيانة ووافق على العروض', color: 'text-purple-600', bg: 'bg-purple-50' },
            { icon: Heart, label: 'احفظ السيارات المفضلة لديك', color: 'text-red-500', bg: 'bg-red-50' },
            { icon: ShieldCheck, label: 'احصل على تقارير فحص موثوقة', color: 'text-blue-600', bg: 'bg-blue-50' },
            { icon: Sparkles, label: 'استشر خبير الذكاء الاصطناعي الخاص بك', color: 'text-indigo-600', bg: 'bg-indigo-50' },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-4">
              <div className={`p-2.5 ${item.bg} ${item.color} rounded-xl shrink-0`}>
                <item.icon className="h-4 w-4" />
              </div>
              <span className="text-sm font-bold text-slate-700">{item.label}</span>
            </div>
          ))}
        </div>

        {/* Vendor CTA */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-[2.5rem] p-6 shadow-lg shadow-orange-100 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-32 h-32 bg-white/10 rounded-full -ml-16 -mt-16 pointer-events-none" />
          <div className="relative z-10 flex items-center justify-between gap-4">
            <div>
              <p className="text-white font-extrabold text-lg leading-tight">هل أنت تاجر أو ورشة؟</p>
              <p className="text-orange-100 text-xs mt-1">انضم كمورّد واحصل على لوحة تحكم كاملة</p>
            </div>
            <Link href="/register" className="shrink-0">
              <Button className="rounded-2xl bg-white text-orange-600 hover:bg-orange-50 font-extrabold h-11 px-5 shadow-md gap-2 transition-all hover:scale-[1.02]">
                <Store className="h-4 w-4" />
                سجّل كمورّد
              </Button>
            </Link>
          </div>
        </div>

        {/* Quick explore links */}
        <div className="bg-white rounded-[2.5rem] p-5 shadow-sm space-y-3">
          <p className="text-xs font-extrabold text-slate-400 uppercase tracking-widest">
            تصفح بدون حساب
          </p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { href: '/buy', label: 'سوق السيارات', icon: Car, color: 'text-blue-600 bg-blue-50' },
              { href: '/marketplace', label: 'السوق العام', icon: Search, color: 'text-emerald-600 bg-emerald-50' },
              { href: '/parts', label: 'قطع الغيار', icon: Package, color: 'text-orange-500 bg-orange-50' },
              { href: '/diagnose', label: 'الفحص الذكي', icon: Zap, color: 'text-purple-600 bg-purple-50' },
            ].map((link) => (
              <Link key={link.href} href={link.href}>
                <div className="flex items-center gap-3 p-4 rounded-2xl border border-slate-100 hover:border-blue-200 hover:bg-slate-50 transition-all group cursor-pointer">
                  <div className={`p-2 rounded-xl ${link.color}`}>
                    <link.icon className="h-4 w-4" />
                  </div>
                  <span className="text-sm font-bold text-slate-700 group-hover:text-blue-600 transition-colors">{link.label}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

// ─── Authenticated Profile View ───────────────────────────────────────────────
// This is the original profile page, completely unchanged in structure and behavior.

function AuthenticatedProfileView() {
  const { toast } = useToast();
  const [role, setRole] = useState<'user' | 'vendor'>('user');
  const [isAddPartOpen, setIsAddPartOpen] = useState(false);
  const [isAddServiceOpen, setIsAddServiceOpen] = useState(false);

  const [newPart, setNewPart] = useState({
    name: '',
    price: '',
    category: '',
    customCategory: '',
    sku: '',
    description: ''
  });

  const [myListings, setMyListings] = useState<any[]>([]);
  const [loadingListings, setLoadingListings] = useState(true);

  const [myRepairs, setMyRepairs] = useState<any[]>([]);
  const [loadingRepairs, setLoadingRepairs] = useState(true);
  const [myPurchaseRequests, setMyPurchaseRequests] = useState<any[]>([]);
  const [loadingPurchaseRequests, setLoadingPurchaseRequests] = useState(true);
  const [myCars, setMyCars] = useState<any[]>([]);
  const [loadingCars, setLoadingCars] = useState(true);
  const [selectedCarId, setSelectedCarId] = useState<number | null>(null);
  const [selectedCarTimeline, setSelectedCarTimeline] = useState<any | null>(null);
  const [loadingCarTimeline, setLoadingCarTimeline] = useState(false);
  const [isAddCarOpen, setIsAddCarOpen] = useState(false);
  const [submittingCar, setSubmittingCar] = useState(false);
  const [sendingCarMessage, setSendingCarMessage] = useState(false);
  const [newCarMessage, setNewCarMessage] = useState('');
  const [newCar, setNewCar] = useState({
    nickname: '',
    make: '',
    model: '',
    year: '',
    plate_number: '',
    vin: '',
    current_mileage: '',
    oil_change_interval_km: '5000',
  });

  const API = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

  // Read real user from localStorage
  const [userData, setUserData] = useState<{ full_name: string; email: string; role: string } | null>(null);

  useEffect(() => {
    const parsed = readStoredUser();
    if (parsed) {
      setUserData(parsed as any);
      if (normalizeRole(parsed.role as string | undefined) === 'vendor') setRole('vendor');
      else setRole('user');
    }
  }, []);

  const fetchMyCars = async () => {
    setLoadingCars(true);
    try {
      const token = tokenStore.getAccess();
      if (!token) return;
      const res = await fetch(`${API}/api/workshops/cars/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      const cars = data.results || data || [];
      setMyCars(cars);
      setSelectedCarId((prev) => {
        if (prev && cars.some((car: any) => car.id === prev)) return prev;
        return cars[0]?.id ?? null;
      });
    } catch {
      setMyCars([]);
    } finally {
      setLoadingCars(false);
    }
  };

  const fetchCarTimeline = async (carId: number) => {
    setLoadingCarTimeline(true);
    try {
      const token = tokenStore.getAccess();
      if (!token) return;
      const res = await fetch(`${API}/api/workshops/cars/${carId}/timeline/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      setSelectedCarTimeline(data);
    } catch {
      setSelectedCarTimeline(null);
    } finally {
      setLoadingCarTimeline(false);
    }
  };

  useEffect(() => {
    const fetchMyListings = async () => {
      setLoadingListings(true);
      try {
        const token = tokenStore.getAccess();
        if (!token) return;
        const res = await fetch(`${API}/api/marketplace/public-listings/?my_listings=true`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setMyListings(data.results || data);
        }
      } catch (err) { } finally { setLoadingListings(false); }
    };

    const fetchMyRepairs = async () => {
      setLoadingRepairs(true);
      try {
        const token = tokenStore.getAccess();
        if (!token) return;
        const res = await fetch(`${API}/api/workshops/job-cards/`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setMyRepairs(data.results || data);
        }
      } catch (err) { } finally { setLoadingRepairs(false); }
    };

    const fetchMyPurchaseRequests = async () => {
      setLoadingPurchaseRequests(true);
      try {
        const token = tokenStore.getAccess();
        if (!token) return;
        const res = await fetch(`${API}/api/marketplace/purchase-requests/`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setMyPurchaseRequests(data.results || data || []);
        }
      } catch (err) { } finally { setLoadingPurchaseRequests(false); }
    };

    if (role === 'user') {
      fetchMyListings();
      fetchMyRepairs();
      fetchMyPurchaseRequests();
      fetchMyCars();
    }
  }, [role]);

  useEffect(() => {
    if (role !== 'user') return;
    if (!selectedCarId) {
      setSelectedCarTimeline(null);
      return;
    }
    fetchCarTimeline(selectedCarId);
  }, [role, selectedCarId]);

  const handleAddItem = (type: string) => {
    const finalCategory = newPart.category === 'new' ? newPart.customCategory : newPart.category;

    if (type === 'قطعة غيار' && (!newPart.name || !finalCategory || !newPart.price)) {
      toast({
        variant: "destructive",
        title: "بيانات ناقصة",
        description: "يرجى ملء الاسم، الفئة، والسعر على الأقل.",
      });
      return;
    }

    toast({
      title: "تمت الإضافة بنجاح",
      description: `تمت إضافة ${type} جديد بنجاح. الصنف: ${finalCategory || 'غير محدد'}`,
    });

    setIsAddPartOpen(false);
    setIsAddServiceOpen(false);
    setNewPart({ name: '', price: '', category: '', customCategory: '', sku: '', description: '' });
  };

  const handleCancelPurchaseRequest = async (requestId: number) => {
    if (!confirm('هل تريد إلغاء طلب الشراء هذا؟')) return;
    try {
      const token = tokenStore.getAccess();
      if (!token) return;
      const API = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
      const res = await fetch(`${API}/api/marketplace/purchase-requests/${requestId}/cancel/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error();
      setMyPurchaseRequests((prev) => prev.map((req) => (
        req.id === requestId ? { ...req, status: 'cancelled' } : req
      )));
      toast({ title: '✅ تم إلغاء طلب الشراء' });
    } catch {
      toast({ variant: 'destructive', title: 'خطأ', description: 'تعذر إلغاء الطلب حالياً.' });
    }
  };

  const handleAddCar = async () => {
    if (!newCar.make || !newCar.model) {
      toast({
        variant: 'destructive',
        title: 'بيانات ناقصة',
        description: 'يرجى إدخال الماركة والموديل على الأقل.',
      });
      return;
    }

    setSubmittingCar(true);
    try {
      const token = tokenStore.getAccess();
      if (!token) throw new Error('unauthorized');

      const payload: any = {
        nickname: newCar.nickname,
        make: newCar.make,
        model: newCar.model,
        year: newCar.year ? Number(newCar.year) : null,
        plate_number: newCar.plate_number,
        vin: newCar.vin,
        current_mileage: newCar.current_mileage ? Number(newCar.current_mileage) : null,
        oil_change_interval_km: newCar.oil_change_interval_km ? Number(newCar.oil_change_interval_km) : 5000,
      };

      const res = await fetch(`${API}/api/workshops/cars/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('failed');

      toast({ title: '✅ تمت إضافة المركبة', description: 'تم حفظ المركبة في سجل الصيانة.' });
      setIsAddCarOpen(false);
      setNewCar({
        nickname: '',
        make: '',
        model: '',
        year: '',
        plate_number: '',
        vin: '',
        current_mileage: '',
        oil_change_interval_km: '5000',
      });
      await fetchMyCars();
    } catch {
      toast({
        variant: 'destructive',
        title: 'خطأ',
        description: 'تعذر إضافة المركبة حالياً.',
      });
    } finally {
      setSubmittingCar(false);
    }
  };

  const handleSendCarMessage = async () => {
    if (!selectedCarId || !newCarMessage.trim()) return;
    setSendingCarMessage(true);
    try {
      const token = tokenStore.getAccess();
      if (!token) throw new Error('unauthorized');
      const res = await fetch(`${API}/api/workshops/cars/${selectedCarId}/messages/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message: newCarMessage.trim() }),
      });
      if (!res.ok) throw new Error('failed');
      setNewCarMessage('');
      await fetchCarTimeline(selectedCarId);
    } catch {
      toast({ variant: 'destructive', title: 'خطأ', description: 'تعذر إرسال الرسالة.' });
    } finally {
      setSendingCarMessage(false);
    }
  };

  // Derive display name and email from real data, fall back to role-based defaults
  const displayName = userData?.full_name || (role === 'user' ? 'محمد بن فهد' : 'معرض النخبة للسيارات والخدمات');
  const displayEmail = userData?.email
    ? (userData.email.includes('@public.sayarti360.local') ? userData.email.split('@')[0] : userData.email)
    : (role === 'user' ? 'm.fahad@email.com' : 'v.elite@carestore.com');
  const vendorType = ((userData as any)?.store?.vendor_type || (userData as any)?.vendor_type || '') as string;
  const isShowroomVendor = role === 'vendor' && vendorType === 'showroom';

  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .slice(0, 2)
    .join('');
  const selectedCar = myCars.find((car: any) => car.id === selectedCarId) || null;
  const selectedCarHealth = selectedCarTimeline?.health || selectedCar?.health;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navigation title="الملف الشخصي" />

      <div className="p-4 max-w-2xl mx-auto space-y-6 pb-24">
        <div className="bg-white rounded-[2.5rem] p-8 shadow-sm flex flex-col items-center text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-full -mr-16 -mt-16 opacity-50" />

          <Avatar className="h-32 w-32 border-4 border-white shadow-xl mb-4 relative z-10">
            <AvatarImage src={role === 'user' ? "https://picsum.photos/seed/user/200" : "https://picsum.photos/seed/vendor/200"} />
            <AvatarFallback>{initials || (role === 'user' ? 'U' : 'V')}</AvatarFallback>
          </Avatar>

          <h2 className="text-2xl font-extrabold text-slate-800 mb-1">{displayName}</h2>
          <p className="text-slate-400 text-sm mb-6">{displayEmail}</p>

          <div className="flex gap-2">
            <Badge className={`px-4 py-1.5 rounded-full text-xs font-bold ${role === 'user' ? 'bg-blue-600' : (role === 'vendor' ? 'bg-orange-500' : 'bg-purple-600')}`}>
              {role === 'user' ? 'مستخدم عادي' : (role === 'vendor' ? 'تاجر / صاحب ورشة معتمد' : 'مدير النظام')}
            </Badge>
          </div>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className={`bg-white p-1 rounded-2xl h-14 shadow-sm w-full flex overflow-x-auto gap-2 no-scrollbar`}>
            <TabsTrigger value="overview" className="flex-1 min-w-[100px] rounded-xl data-[state=active]:bg-blue-600 data-[state=active]:text-white transition-all text-sm font-bold">
              {role === 'user' ? 'لوحة التحكم' : 'الإحصائيات'}
            </TabsTrigger>
            {role === 'vendor' && (
              <TabsTrigger value="management" className="flex-1 min-w-[100px] rounded-xl data-[state=active]:bg-orange-500 data-[state=active]:text-white transition-all text-sm font-bold">
                إدارة المتجر
              </TabsTrigger>
            )}
            {role === 'user' && (
              <>
                <TabsTrigger value="listings" className="flex-1 min-w-[100px] rounded-xl data-[state=active]:bg-emerald-500 data-[state=active]:text-white transition-all text-sm font-bold">
                  إعلاناتي
                </TabsTrigger>
                <TabsTrigger value="purchase_requests" className="flex-1 min-w-[120px] rounded-xl data-[state=active]:bg-indigo-600 data-[state=active]:text-white transition-all text-sm font-bold">
                  طلبات الشراء
                </TabsTrigger>
                <TabsTrigger value="repairs" className="flex-1 min-w-[100px] rounded-xl data-[state=active]:bg-purple-600 data-[state=active]:text-white transition-all text-sm font-bold">
                  الصيانة
                </TabsTrigger>
              </>
            )}
            <TabsTrigger value="settings" className="flex-1 min-w-[100px] rounded-xl data-[state=active]:bg-blue-600 data-[state=active]:text-white transition-all text-sm font-bold">
              الإعدادات
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {role === 'user' ? (
                <>
                  <ProfileCard icon={Heart} label="المفضلة" count="12" color="text-red-500" />
                  <ProfileCard
                    icon={ClipboardList}
                    label="طلبات الشراء"
                    count={loadingPurchaseRequests ? '...' : String(myPurchaseRequests.length)}
                    color="text-indigo-600"
                  />
                  <ProfileCard icon={Car} label="حجوزات الإيجار" count="—" color="text-violet-600" href="/rentals/bookings" />
                  <ProfileCard icon={History} label="عمليات البحث" count="45" color="text-slate-500" />
                  <ProfileCard icon={ShieldCheck} label="الفحوصات" count="1" color="text-green-500" />
                </>
              ) : (
                isShowroomVendor ? (
                  <>
                    <ProfileCard icon={Car} label="لوحة المعرض" count="ERP" color="text-indigo-600" href="/showroom" />
                    <ProfileCard icon={Briefcase} label="إدارة السيارات" count="مباشر" color="text-orange-500" href="/showroom?tab=cars" />
                    <ProfileCard icon={ClipboardList} label="المبيعات" count="مباشر" color="text-blue-500" href="/showroom?tab=orders" />
                    <ProfileCard icon={CreditCard} label="التقارير" count="مباشر" color="text-green-500" href="/showroom?tab=reports" />
                  </>
                ) : (
                  <>
                    <ProfileCard icon={Briefcase} label="إعلاناتي" count="24" color="text-orange-500" href="/vendor/inventory" />
                    <ProfileCard icon={ClipboardList} label="الطلبات" count="8" color="text-blue-500" href="/vendor/invoices" />
                    <ProfileCard icon={Store} label="تقييم المتجر" count="4.9" color="text-yellow-500" href="/vendor/settings" />
                    <ProfileCard icon={CreditCard} label="الرصيد" count="5,400 ر.س" color="text-green-500" href="/vendor" />
                  </>
                )
              )}
            </div>

            <Link href="/reports">
              <Card className="flex items-center justify-between p-5 border-none shadow-sm bg-white rounded-[2.5rem] hover:bg-slate-50 transition-colors mt-4">
                <div className="flex items-center gap-4">
                  <div className="p-4 rounded-2xl bg-indigo-50 text-indigo-600">
                    <FileText className="h-7 w-7" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-lg font-bold text-slate-800">سجل التقارير</span>
                    <span className="text-[10px] text-slate-400 font-bold uppercase">كراجي الذكي</span>
                  </div>
                </div>
                <div className="h-10 w-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-300">
                  <ChevronLeft className="h-5 w-5" />
                </div>
              </Card>
            </Link>
          </TabsContent>

          {role === 'vendor' && (
            <TabsContent value="management" className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                {isShowroomVendor ? (
                  <>
                    <Link href="/showroom">
                      <Button className="w-full h-24 rounded-3xl bg-white border-2 border-slate-100 text-slate-700 flex flex-col gap-2 hover:bg-slate-50 hover:border-indigo-500/50 transition-all shadow-sm">
                        <Banknote className="h-6 w-6 text-indigo-500" />
                        <span className="font-bold text-xs">لوحة المعرض</span>
                      </Button>
                    </Link>
                    <Link href="/showroom?tab=cars">
                      <Button className="w-full h-24 rounded-3xl bg-white border-2 border-slate-100 text-slate-700 flex flex-col gap-2 hover:bg-slate-50 hover:border-orange-500/50 transition-all shadow-sm">
                        <Package className="h-6 w-6 text-orange-500" />
                        <span className="font-bold text-xs">إدارة السيارات</span>
                      </Button>
                    </Link>
                    <Link href="/showroom?tab=orders">
                      <Button className="w-full h-24 rounded-3xl bg-white border-2 border-slate-100 text-slate-700 flex flex-col gap-2 hover:bg-slate-50 hover:border-blue-500/50 transition-all shadow-sm">
                        <Receipt className="h-6 w-6 text-blue-500" />
                        <span className="font-bold text-xs">المبيعات والفواتير</span>
                      </Button>
                    </Link>
                    <Link href="/showroom?tab=settings">
                      <Button className="w-full h-24 rounded-3xl bg-white border-2 border-slate-100 text-slate-700 flex flex-col gap-2 hover:bg-slate-50 hover:border-slate-500/50 transition-all shadow-sm">
                        <Settings className="h-6 w-6 text-slate-500" />
                        <span className="font-bold text-xs">إعدادات المعرض</span>
                      </Button>
                    </Link>
                    <Link href="/showroom?tab=advanced">
                      <Button className="w-full h-24 rounded-3xl bg-white border-2 border-slate-100 text-slate-700 flex flex-col gap-2 hover:bg-slate-50 hover:border-violet-500/50 transition-all shadow-sm">
                        <Car className="h-6 w-6 text-violet-600" />
                        <span className="font-bold text-xs">خيارات متقدمة</span>
                      </Button>
                    </Link>
                  </>
                ) : (
                  <>
                    <Link href="/vendor">
                      <Button className="w-full h-24 rounded-3xl bg-white border-2 border-slate-100 text-slate-700 flex flex-col gap-2 hover:bg-slate-50 hover:border-cyan-500/50 transition-all shadow-sm">
                        <Briefcase className="h-6 w-6 text-cyan-600" />
                        <span className="font-bold text-xs">لوحة المورد</span>
                      </Button>
                    </Link>
                    <Link href="/vendor/pos">
                      <Button className="w-full h-24 rounded-3xl bg-white border-2 border-slate-100 text-slate-700 flex flex-col gap-2 hover:bg-slate-50 hover:border-indigo-500/50 transition-all shadow-sm">
                        <Banknote className="h-6 w-6 text-indigo-500" />
                        <span className="font-bold text-xs">نقطة البيع (POS)</span>
                      </Button>
                    </Link>
                    <Link href="/vendor/inventory">
                      <Button className="w-full h-24 rounded-3xl bg-white border-2 border-slate-100 text-slate-700 flex flex-col gap-2 hover:bg-slate-50 hover:border-orange-500/50 transition-all shadow-sm">
                        <Package className="h-6 w-6 text-orange-500" />
                        <span className="font-bold text-xs">إدارة المخزون</span>
                      </Button>
                    </Link>
                    <Link href="/vendor/invoices">
                      <Button className="w-full h-24 rounded-3xl bg-white border-2 border-slate-100 text-slate-700 flex flex-col gap-2 hover:bg-slate-50 hover:border-blue-500/50 transition-all shadow-sm">
                        <Receipt className="h-6 w-6 text-blue-500" />
                        <span className="font-bold text-xs">سجل المبيعات والفواتير</span>
                      </Button>
                    </Link>
                    <Link href="/vendor/settings">
                      <Button className="w-full h-24 rounded-3xl bg-white border-2 border-slate-100 text-slate-700 flex flex-col gap-2 hover:bg-slate-50 hover:border-slate-500/50 transition-all shadow-sm">
                        <Settings className="h-6 w-6 text-slate-500" />
                        <span className="font-bold text-xs">إعدادات المنشأة والفواتير</span>
                      </Button>
                    </Link>
                    <Link href="/vendor/rentals">
                      <Button className="w-full h-24 rounded-3xl bg-white border-2 border-slate-100 text-slate-700 flex flex-col gap-2 hover:bg-slate-50 hover:border-violet-500/50 transition-all shadow-sm">
                        <Car className="h-6 w-6 text-violet-600" />
                        <span className="font-bold text-xs">لوحة تأجير السيارات</span>
                      </Button>
                    </Link>
                  </>
                )}
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between px-2">
                  <h3 className="font-bold text-slate-800 flex items-center gap-2">
                    <ClipboardList className="h-5 w-5 text-blue-600" /> طلبات الزبائن الواردة
                  </h3>
                  <Badge variant="outline" className="text-[10px] rounded-full">آخر 24 ساعة</Badge>
                </div>

                <div className="space-y-3">
                  {MOCK_VENDOR_ORDERS.map((order) => (
                    <Card key={order.id} className="border-none shadow-sm rounded-[2rem] bg-white overflow-hidden group">
                      <CardContent className="p-5 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`p-3 rounded-2xl ${order.status === 'pending' ? 'bg-amber-50 text-amber-500' : 'bg-green-50 text-green-500'}`}>
                            <Package className="h-6 w-6" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-bold text-sm text-slate-800">{order.customer}</h4>
                              <span className="text-[10px] text-slate-400">#{order.id}</span>
                            </div>
                            <p className="text-xs text-slate-500 font-medium">{order.item}</p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <p className="font-bold text-secondary text-sm">{order.price} ر.س</p>
                          <Link href={`/chat?id=${order.chatId}`}>
                            <Button size="sm" variant="outline" className="rounded-full h-8 px-3 flex gap-2 border-blue-200 text-blue-600 hover:bg-blue-50">
                              <MessageCircle className="h-3.5 w-3.5" />
                              <span className="text-[10px]">مراسلة</span>
                            </Button>
                          </Link>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </TabsContent>
          )}

          {role === 'user' && (
            <TabsContent value="listings" className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-slate-800 flex items-center gap-2 text-xl">
                    <Tag className="h-6 w-6 text-emerald-600" /> إعلاناتي المبوبة
                  </h3>
                  <p className="text-slate-500 text-sm mt-1">تتبع إعلاناتك العامة وحالة النشر</p>
                </div>
                <Link href="/marketplace/new">
                  <Button className="rounded-2xl bg-emerald-500 hover:bg-emerald-600 shadow-md gap-2 h-12 px-6">
                    <PlusCircle className="w-5 h-5" /> إعلان جديد
                  </Button>
                </Link>
              </div>
              <Card className="rounded-[2.5rem] border-none shadow-sm bg-white p-6 text-center">
                {loadingListings ? (
                  <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" /></div>
                ) : myListings.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 opacity-60">
                    <Package className="w-16 h-16 text-slate-300 mb-4" />
                    <p className="text-slate-500 font-medium text-lg">لا توجد لديك إعلانات حالياً.</p>
                    <p className="text-slate-400 text-sm mt-1">ابدأ بإضافة أول إعلان لك في السوق العام.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-right">
                    {myListings.map((listing: any) => (
                      <Card key={listing.id} className="overflow-hidden border-2 border-slate-100 shadow-sm rounded-3xl hover:border-emerald-500/50 transition-colors flex flex-col">
                        <div className="h-40 bg-slate-100 relative shrink-0">
                          {listing.images && listing.images.length > 0 ? (
                            <img src={listing.images[0].image} className="w-full h-full object-cover" alt="listing" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-300"><Store className="h-10 w-10" /></div>
                          )}
                          <div className="absolute top-3 right-3 flex gap-2">
                            <Badge className={listing.status === 'active' ? 'bg-emerald-500' : listing.status === 'sold' ? 'bg-slate-500' : listing.status === 'rejected' ? 'bg-red-500' : 'bg-amber-500'}>
                              {listing.status === 'active' ? 'نشط' : listing.status === 'sold' ? 'مباع' : listing.status === 'rejected' ? 'مرفوض' : 'قيد المراجعة'}
                            </Badge>
                          </div>
                        </div>
                        <CardContent className="p-4 space-y-2 flex-grow flex flex-col">
                          <h4 className="font-bold text-slate-800 line-clamp-1">{listing.title}</h4>
                          <p className="font-bold text-emerald-600">{listing.price} ر.س</p>
                          <div className="flex items-center gap-2 mt-auto pt-4 border-t border-slate-50">
                            {listing.status === 'active' && (
                              <Button
                                size="sm" variant="outline"
                                className="h-8 rounded-xl text-[10px] font-bold border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                                onClick={async () => {
                                  if (!confirm('هل أنت متأكد من تمييز هذا الغرض كمباع؟')) return;
                                  const API = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
                                  const res = await fetch(`${API}/api/marketplace/public-listings/${listing.id}/mark_sold/`, {
                                    method: 'POST',
                                    headers: { Authorization: `Bearer ${tokenStore.getAccess()}` }
                                  });
                                  if (res.ok) {
                                    toast({ title: '✅ تم التمييز كمباع' });
                                    window.location.reload();
                                  }
                                }}
                              >
                                تم البيع
                              </Button>
                            )}
                            <Button
                              size="sm" variant="outline"
                              className="h-8 rounded-xl text-[10px] font-bold border-red-200 text-red-600 hover:bg-red-50"
                              onClick={async () => {
                                if (!confirm('هل أنت متأكد من حذف هذا الإعلان؟')) return;
                                const API = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
                                const res = await fetch(`${API}/api/marketplace/public-listings/${listing.id}/`, {
                                  method: 'DELETE',
                                  headers: { Authorization: `Bearer ${tokenStore.getAccess()}` }
                                });
                                if (res.ok) {
                                  toast({ title: '🗑️ تم حذف الإعلان' });
                                  window.location.reload();
                                }
                              }}
                            >
                              حذف
                            </Button>
                            <span className="text-[10px] text-slate-400 mr-auto">{new Date(listing.created_at).toLocaleDateString()}</span>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </Card>
            </TabsContent>
          )}

          {role === 'user' && (
            <TabsContent value="purchase_requests" className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-slate-800 flex items-center gap-2 text-xl">
                    <ClipboardList className="h-6 w-6 text-indigo-600" /> طلبات الشراء
                  </h3>
                  <p className="text-slate-500 text-sm mt-1">هنا يمكنك متابعة حالة طلبات الشراء التي أرسلتها للبائعين.</p>
                </div>
                <Link href="/buy">
                  <Button className="rounded-2xl bg-indigo-600 hover:bg-indigo-700 shadow-md gap-2 h-12 px-6">
                    <Search className="w-4 h-4" /> تصفح سيارات
                  </Button>
                </Link>
              </div>

              <Card className="rounded-[2.5rem] border-none shadow-sm bg-white p-6 text-center">
                {loadingPurchaseRequests ? (
                  <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full" /></div>
                ) : myPurchaseRequests.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 opacity-70">
                    <ClipboardList className="w-16 h-16 text-slate-300 mb-4" />
                    <p className="text-slate-500 font-medium text-lg">لا توجد طلبات شراء حتى الآن.</p>
                    <p className="text-slate-400 text-sm mt-1">عند إرسال طلب شراء من صفحة السيارة سيظهر هنا مباشرة.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-right">
                    {myPurchaseRequests.map((req: any) => {
                      const statusMap: Record<string, { label: string; cls: string }> = {
                        pending: { label: 'قيد المراجعة', cls: 'bg-amber-100 text-amber-800' },
                        accepted: { label: 'تم القبول', cls: 'bg-emerald-100 text-emerald-800' },
                        rejected: { label: 'مرفوض', cls: 'bg-red-100 text-red-700' },
                        cancelled: { label: 'ملغى', cls: 'bg-slate-100 text-slate-600' },
                      };
                      const mapped = statusMap[req.status] || statusMap.pending;
                      return (
                        <Card key={req.id} className="rounded-3xl border border-slate-100 shadow-sm p-4 space-y-3">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <h4 className="font-bold text-slate-800 line-clamp-1">{req.listing_title || 'طلب شراء سيارة'}</h4>
                              <p className="text-xs text-slate-400 mt-1">البائع: {req.seller_name || 'غير متوفر'}</p>
                            </div>
                            <Badge className={`rounded-full ${mapped.cls}`}>{mapped.label}</Badge>
                          </div>

                          <div className="text-sm text-slate-600 space-y-1">
                            <p>السعر المقترح: <span className="font-bold text-indigo-700">{req.offered_price ? `${req.offered_price} ر.س` : 'غير محدد'}</span></p>
                            <p className="text-xs text-slate-400">تاريخ الطلب: {req.created_at ? new Date(req.created_at).toLocaleDateString('ar-SA') : '—'}</p>
                          </div>

                          <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                            {req.listing && (
                              <Link href={`/buy/listing/${req.listing}`}>
                                <Button size="sm" variant="outline" className="rounded-xl h-8">فتح الإعلان</Button>
                              </Link>
                            )}
                            {req.status === 'pending' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="rounded-xl h-8 border-red-200 text-red-600 hover:bg-red-50"
                                onClick={() => handleCancelPurchaseRequest(req.id)}
                              >
                                إلغاء الطلب
                              </Button>
                            )}
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </Card>
            </TabsContent>
          )}

          {role === 'user' && (
            <TabsContent value="repairs" className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-slate-800 flex items-center gap-2 text-xl">
                    <Wrench className="h-6 w-6 text-purple-600" /> مركباتي والصيانة
                  </h3>
                  <p className="text-slate-500 text-sm mt-1">سجل صحي شامل لكل مركبة مع تذكيرات الصيانة وتحديثات الورشة.</p>
                </div>
                <Button onClick={() => setIsAddCarOpen(true)} className="rounded-2xl bg-purple-600 hover:bg-purple-700 shadow-md gap-2 h-11 px-5">
                  <PlusCircle className="w-4 h-4" /> إضافة مركبة
                </Button>
              </div>

              <Card className="rounded-[2.5rem] border-none shadow-sm bg-white p-6">
                {loadingCars ? (
                  <div className="flex justify-center py-10"><div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full" /></div>
                ) : myCars.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 text-center opacity-70">
                    <Car className="w-14 h-14 text-slate-300 mb-3" />
                    <p className="text-slate-600 font-bold">لا توجد مركبات مسجلة في ملفك.</p>
                    <p className="text-slate-400 text-sm mt-1">أضف مركبتك الآن لبدء تتبع السجل الصحي والصيانة.</p>
                    <Button size="sm" className="rounded-xl bg-purple-600 hover:bg-purple-700 mt-4" onClick={() => setIsAddCarOpen(true)}>
                      إضافة مركبة
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {myCars.map((car: any) => {
                      const isSelected = selectedCarId === car.id;
                      const health = car.health?.health_score || 0;
                      return (
                        <button
                          key={car.id}
                          type="button"
                          onClick={() => setSelectedCarId(car.id)}
                          className={`text-right border rounded-3xl p-4 transition-all ${isSelected ? 'border-purple-400 bg-purple-50/60 shadow-sm' : 'border-slate-100 hover:border-purple-200 hover:bg-slate-50'}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div>
                              <p className="font-bold text-slate-800">{car.make} {car.model} {car.year || ''}</p>
                              <p className="text-xs text-slate-500 mt-1">لوحة: {car.plate_number || 'غير محدد'}</p>
                              {car.nickname && <p className="text-xs text-purple-600 mt-1">{car.nickname}</p>}
                            </div>
                            <Badge className={health >= 80 ? 'bg-emerald-500 text-white' : health >= 60 ? 'bg-amber-500 text-white' : 'bg-red-500 text-white'}>
                              صحة السيارة {health}%
                            </Badge>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-3 text-[11px]">
                            <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-600">العداد: {car.current_mileage || 0} كم</span>
                            {car.next_oil_change_km && (
                              <span className="px-2 py-1 rounded-full bg-blue-50 text-blue-700">تغيير الزيت عند {car.next_oil_change_km} كم</span>
                            )}
                            {car.next_visit_due && (
                              <span className="px-2 py-1 rounded-full bg-purple-100 text-purple-700">الزيارة القادمة {car.next_visit_due}</span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </Card>

              {selectedCar && (
                <Card className="rounded-[2.5rem] border-none shadow-sm bg-white p-6 space-y-5">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <h4 className="font-bold text-slate-800 text-lg">السجل الصحي للمركبة: {selectedCar.make} {selectedCar.model}</h4>
                    <Badge className={`${(selectedCarHealth?.health_score || 0) >= 80 ? 'bg-emerald-500' : (selectedCarHealth?.health_score || 0) >= 60 ? 'bg-amber-500' : 'bg-red-500'} text-white`}>
                      {selectedCarHealth?.health_score || 0}% مؤشر الصحة
                    </Badge>
                  </div>

                  {loadingCarTimeline ? (
                    <div className="flex justify-center py-10"><div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full" /></div>
                  ) : !selectedCarTimeline ? (
                    <p className="text-sm text-slate-500">لا توجد تفاصيل إضافية حالياً لهذه المركبة.</p>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div className="rounded-2xl bg-slate-50 p-3">
                          <p className="text-slate-400 text-xs">إجمالي تكلفة الصيانة</p>
                          <p className="font-bold text-slate-800">{selectedCarHealth?.total_maintenance_cost || '0'} ر.س</p>
                        </div>
                        <div className="rounded-2xl bg-slate-50 p-3">
                          <p className="text-slate-400 text-xs">سجلات مفتوحة</p>
                          <p className="font-bold text-slate-800">{selectedCarHealth?.open_records || 0}</p>
                        </div>
                        <div className="rounded-2xl bg-slate-50 p-3">
                          <p className="text-slate-400 text-xs">الزيارة القادمة</p>
                          <p className="font-bold text-slate-800">{selectedCarHealth?.next_visit_due || 'غير محدد'}</p>
                        </div>
                        <div className="rounded-2xl bg-slate-50 p-3">
                          <p className="text-slate-400 text-xs">رمز المركبة (QR)</p>
                          <p className="font-bold text-slate-700 text-xs break-all">{selectedCar.public_code}</p>
                        </div>
                      </div>

                      {Array.isArray(selectedCarHealth?.insights) && selectedCarHealth.insights.length > 0 && (
                        <div className="rounded-2xl border border-purple-100 bg-purple-50/50 p-4">
                          <p className="font-bold text-purple-700 mb-2">توصيات الصيانة الذكية</p>
                          <div className="space-y-1">
                            {selectedCarHealth.insights.map((insight: string, idx: number) => (
                              <p key={`${insight}-${idx}`} className="text-sm text-slate-700">• {insight}</p>
                            ))}
                          </div>
                        </div>
                      )}

                      <div>
                        <p className="font-bold text-slate-700 mb-3">التذكيرات القادمة</p>
                        {selectedCarTimeline.reminders?.length ? (
                          <div className="flex flex-wrap gap-2">
                            {selectedCarTimeline.reminders.map((reminder: any) => (
                              <span key={reminder.id} className="px-3 py-1 rounded-full text-xs bg-amber-100 text-amber-800">
                                {reminder.title} {reminder.due_date ? `(${reminder.due_date})` : reminder.due_mileage ? `(${reminder.due_mileage} كم)` : ''}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-slate-500">لا توجد تذكيرات حالياً.</p>
                        )}
                      </div>

                      <div>
                        <p className="font-bold text-slate-700 mb-3">الخط الزمني للصيانة</p>
                        {selectedCarTimeline.maintenance_records?.length ? (
                          <div className="space-y-3">
                            {selectedCarTimeline.maintenance_records.map((record: any) => (
                              <div key={record.id} className="border border-slate-100 rounded-2xl p-4 space-y-2">
                                <div className="flex items-center justify-between flex-wrap gap-2">
                                  <p className="font-bold text-slate-800">{record.title || record.service_type}</p>
                                  <Badge className={record.status === 'completed' ? 'bg-emerald-500 text-white' : record.status === 'in_progress' ? 'bg-blue-500 text-white' : 'bg-amber-100 text-amber-800'}>
                                    {record.status}
                                  </Badge>
                                </div>
                                <p className="text-xs text-slate-500">
                                  {record.workshop_name ? `الورشة: ${record.workshop_name}` : 'الورشة: غير محدد'} • {record.happened_at ? new Date(record.happened_at).toLocaleDateString('ar-SA') : '—'}
                                </p>
                                {record.notes && <p className="text-sm text-slate-700">{record.notes}</p>}
                                {record.recommendations && <p className="text-sm text-purple-700">توصية: {record.recommendations}</p>}
                                <div className="flex flex-wrap gap-2 text-xs">
                                  {record.current_mileage && <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-600">عداد: {record.current_mileage} كم</span>}
                                  {record.next_oil_change_km && <span className="px-2 py-1 rounded-full bg-blue-50 text-blue-700">زيت: {record.next_oil_change_km} كم</span>}
                                  {record.service_cost && <span className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-700">التكلفة: {record.service_cost} ر.س</span>}
                                </div>
                                {record.attachments?.length > 0 && (
                                  <div className="flex flex-wrap gap-2 pt-1">
                                    {record.attachments.map((file: any) => (
                                      <a key={file.id} href={file.file} target="_blank" rel="noreferrer" className="text-xs px-2 py-1 rounded-lg bg-slate-50 border border-slate-200 text-slate-700 hover:bg-slate-100">
                                        تقرير/ملف #{file.id}
                                      </a>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-slate-500">لا توجد سجلات صيانة مفصلة بعد.</p>
                        )}
                      </div>

                      <div>
                        <p className="font-bold text-slate-700 mb-3">المحادثة مع الورشة</p>
                        <div className="border border-slate-100 rounded-2xl p-3 space-y-3">
                          <div className="max-h-56 overflow-y-auto space-y-2 pr-1">
                            {selectedCarTimeline.car?.recent_messages?.length ? (
                              selectedCarTimeline.car.recent_messages.map((msg: any) => (
                                <div key={msg.id} className={`p-2 rounded-xl text-sm ${msg.sender_role === 'customer' ? 'bg-blue-50 text-blue-800 mr-8' : 'bg-slate-100 text-slate-700 ml-8'}`}>
                                  <p className="font-bold text-[11px] mb-0.5">{msg.sender_name || msg.sender_role || 'مستخدم'}</p>
                                  <p>{msg.message}</p>
                                </div>
                              ))
                            ) : (
                              <p className="text-sm text-slate-500">لا توجد رسائل بعد.</p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Input
                              value={newCarMessage}
                              onChange={(e) => setNewCarMessage(e.target.value)}
                              placeholder="اكتب رسالة للفني أو الورشة..."
                              className="rounded-xl"
                            />
                            <Button
                              onClick={handleSendCarMessage}
                              disabled={sendingCarMessage || !newCarMessage.trim()}
                              className="rounded-xl bg-blue-600 hover:bg-blue-700"
                            >
                              {sendingCarMessage ? 'جارٍ...' : 'إرسال'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </Card>
              )}

              <Card className="rounded-[2.5rem] border-none shadow-sm bg-white p-6 text-center">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-bold text-slate-800">طلبات الصيانة الحالية</h4>
                  <Link href="/services">
                    <Button size="sm" className="rounded-xl bg-purple-600 hover:bg-purple-700">استكشاف خدمات الصيانة</Button>
                  </Link>
                </div>

                {loadingRepairs ? (
                  <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full" /></div>
                ) : myRepairs.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 opacity-60">
                    <Wrench className="w-12 h-12 text-slate-300 mb-3" />
                    <p className="text-slate-500 font-medium">لا توجد كروت عمل نشطة حالياً.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-right">
                    {myRepairs.map((repair: any) => (
                      <Card key={repair.id} className="overflow-hidden border-2 border-slate-100 shadow-sm rounded-3xl hover:border-purple-500/50 transition-colors flex flex-col p-4 space-y-4 text-right" dir="rtl">
                        <div className="flex items-center justify-between border-b pb-3">
                          <div className="flex items-center gap-3">
                            <div className="p-3 bg-purple-50 text-purple-600 rounded-xl"><Wrench className="w-6 h-6" /></div>
                            <div>
                              <h4 className="font-bold text-slate-800">{repair.vehicle_make} {repair.vehicle_model} {repair.vehicle_year}</h4>
                              <p className="text-xs text-slate-500 font-mono">اللوحة: {repair.vehicle_plate || 'بدون لوحة'}</p>
                            </div>
                          </div>
                          <Badge className={
                            repair.status === 'received' ? 'bg-amber-100 text-amber-800' :
                              repair.status === 'inspecting' ? 'bg-blue-100 text-blue-800' :
                                repair.status === 'waiting_approval' ? 'bg-orange-500 text-white animate-pulse' :
                                  repair.status === 'in_progress' ? 'bg-indigo-500 text-white' :
                                    repair.status === 'ready' ? 'bg-emerald-500 text-white' :
                                      'bg-slate-100 text-slate-800'
                          }>{repair.status}</Badge>
                        </div>

                        {repair.diagnosis_notes && (
                          <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                            <p className="text-xs font-bold text-slate-500 mb-1">ملاحظات الفني لتسعيرة الصيانة:</p>
                            <p className="text-sm text-slate-700">{repair.diagnosis_notes}</p>
                          </div>
                        )}

                        <div className="flex items-center justify-between pt-2">
                          <p className="font-bold flex flex-col">
                            <span className="text-[10px] text-slate-400">إجمالي الفاتورة المبدئية</span>
                            <span className="text-lg text-primary">{repair.total_amount} ر.س</span>
                          </p>
                          {repair.status === 'waiting_approval' && (
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 rounded-xl"
                                onClick={async () => {
                                  if (!confirm('هل تأكد رفض هذه التسعيرة؟')) return;
                                  const res = await fetch(`${API}/api/workshops/job-cards/${repair.id}/reject_quotation/`, {
                                    method: 'POST',
                                    headers: { Authorization: `Bearer ${tokenStore.getAccess()}` }
                                  });
                                  if (res.ok) {
                                    toast({ title: '✅ تم رفض التسعيرة وإيقاف الطلب' });
                                    window.location.reload();
                                  }
                                }}>رفض</Button>
                              <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl shadow-md gap-1"
                                onClick={async () => {
                                  const res = await fetch(`${API}/api/workshops/job-cards/${repair.id}/approve_quotation/`, {
                                    method: 'POST',
                                    headers: { Authorization: `Bearer ${tokenStore.getAccess()}` }
                                  });
                                  if (res.ok) {
                                    toast({ title: '✅ تم الموافقة بنجاح وسيتم بدء العمل' });
                                    window.location.reload();
                                  }
                                }}><ShieldCheck className="w-4 h-4" />موافقة واعتماد</Button>
                            </div>
                          )}
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </Card>

              <Dialog open={isAddCarOpen} onOpenChange={setIsAddCarOpen}>
                <DialogContent className="max-w-lg rounded-3xl" dir="rtl">
                  <DialogHeader>
                    <DialogTitle>إضافة مركبة جديدة</DialogTitle>
                  </DialogHeader>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
                    <div className="space-y-1">
                      <Label>اسم تعريفي (اختياري)</Label>
                      <Input value={newCar.nickname} onChange={(e) => setNewCar({ ...newCar, nickname: e.target.value })} placeholder="سيارة العائلة" className="rounded-xl" />
                    </div>
                    <div className="space-y-1">
                      <Label>الماركة *</Label>
                      <Input value={newCar.make} onChange={(e) => setNewCar({ ...newCar, make: e.target.value })} placeholder="Toyota" className="rounded-xl" />
                    </div>
                    <div className="space-y-1">
                      <Label>الموديل *</Label>
                      <Input value={newCar.model} onChange={(e) => setNewCar({ ...newCar, model: e.target.value })} placeholder="Camry" className="rounded-xl" />
                    </div>
                    <div className="space-y-1">
                      <Label>سنة الصنع</Label>
                      <Input type="number" value={newCar.year} onChange={(e) => setNewCar({ ...newCar, year: e.target.value })} placeholder="2022" className="rounded-xl" />
                    </div>
                    <div className="space-y-1">
                      <Label>رقم اللوحة</Label>
                      <Input value={newCar.plate_number} onChange={(e) => setNewCar({ ...newCar, plate_number: e.target.value })} placeholder="أ ب ج 1234" className="rounded-xl" />
                    </div>
                    <div className="space-y-1">
                      <Label>VIN / الشاصي</Label>
                      <Input value={newCar.vin} onChange={(e) => setNewCar({ ...newCar, vin: e.target.value })} placeholder="اختياري" className="rounded-xl" />
                    </div>
                    <div className="space-y-1">
                      <Label>العداد الحالي (كم)</Label>
                      <Input type="number" value={newCar.current_mileage} onChange={(e) => setNewCar({ ...newCar, current_mileage: e.target.value })} placeholder="125000" className="rounded-xl" />
                    </div>
                    <div className="space-y-1">
                      <Label>فاصل تغيير الزيت (كم)</Label>
                      <Input type="number" value={newCar.oil_change_interval_km} onChange={(e) => setNewCar({ ...newCar, oil_change_interval_km: e.target.value })} placeholder="5000" className="rounded-xl" />
                    </div>
                  </div>
                  <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={() => setIsAddCarOpen(false)} className="rounded-xl">إلغاء</Button>
                    <Button onClick={handleAddCar} disabled={submittingCar} className="rounded-xl bg-purple-600 hover:bg-purple-700">
                      {submittingCar ? 'جارٍ الحفظ...' : 'حفظ المركبة'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </TabsContent>
          )}

          <TabsContent value="settings">
            <Card className="rounded-[2.5rem] border-none shadow-sm bg-white overflow-hidden">
              <div className="p-6 space-y-6">
                <div className="space-y-4">
                  <h3 className="font-bold text-slate-800 flex items-center gap-2">
                    <Settings className="h-5 w-5 text-blue-600" /> إعدادات الحساب
                  </h3>
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-400 mr-1">الاسم الكامل / اسم المنشأة</Label>
                      <Input defaultValue={displayName} className="rounded-2xl bg-slate-50 h-12" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-400 mr-1">رقم الجوال</Label>
                      <Input defaultValue="0501234567" className="rounded-2xl bg-slate-50 h-12 text-left" />
                    </div>
                  </div>
                </div>

                <Button className="w-full h-14 rounded-2xl bg-blue-600 hover:bg-blue-700 text-lg font-extrabold shadow-lg">حفظ التغييرات</Button>

                <Button
                  variant="ghost"
                  className="w-full text-red-500 hover:text-red-600 hover:bg-red-50 font-bold flex items-center justify-center gap-2 py-6"
                  onClick={() => {
                    clearStoredSession();
                    notifySessionChanged();
                    window.location.href = '/';
                  }}
                >
                  <LogOut className="h-5 w-5" /> تسجيل الخروج
                </Button>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ─── Root Component — Auth Gate ───────────────────────────────────────────────

export default function ProfilePage() {
  const router = useRouter();
  // Three states: 'loading' | 'guest' | 'authenticated'
  const [authState, setAuthState] = useState<'loading' | 'guest' | 'authenticated'>('loading');

  useEffect(() => {
    const token = getStoredAccessToken();
    if (!token) {
      setAuthState('guest');
      return;
    }

    const cachedUser = readStoredUser();
    const cachedRole = normalizeRole(cachedUser?.role as string | undefined);
    if (cachedRole === 'admin' || cachedRole === 'superadmin') {
      router.replace('/admin');
      return;
    }
    if (cachedRole === 'customer' || cachedRole === 'vendor') {
      setAuthState('authenticated');
      return;
    }

    const hydrateRole = async () => {
      try {
        const API = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
        const res = await fetch(`${API}/api/auth/me/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          clearStoredSession();
          notifySessionChanged();
          setAuthState('guest');
          return;
        }
        const me = await res.json();
        localStorage.setItem('s360_user', JSON.stringify(me));
        const role = normalizeRole(me.role);
        if (role === 'admin' || role === 'superadmin') {
          router.replace('/admin');
          return;
        }
      } catch {
        clearStoredSession();
        notifySessionChanged();
        setAuthState('guest');
        return;
      }
      setAuthState('authenticated');
    };
    hydrateRole();
  }, [router]);

  // Minimal, non-jarring skeleton while resolving auth on mount
  if (authState === 'loading') {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Navigation title="الملف الشخصي" />
        <div className="p-4 max-w-2xl mx-auto space-y-4 pb-24 animate-pulse">
          <div className="bg-white rounded-[2.5rem] h-56 shadow-sm" />
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-[2rem] h-28 shadow-sm" />
            <div className="bg-white rounded-[2rem] h-28 shadow-sm" />
            <div className="bg-white rounded-[2rem] h-28 shadow-sm" />
            <div className="bg-white rounded-[2rem] h-28 shadow-sm" />
          </div>
        </div>
      </div>
    );
  }

  if (authState === 'guest') {
    return <GuestProfileView />;
  }

  return <AuthenticatedProfileView />;
}

function ProfileCard({
  icon: Icon,
  label,
  count,
  color,
  href,
}: {
  icon: any;
  label: string;
  count: string;
  color: string;
  href?: string;
}) {
  const card = (
    <Card className={`p-5 border-none shadow-sm bg-white rounded-[2rem] transition-transform ${href ? 'hover:scale-105 cursor-pointer hover:bg-slate-50' : ''}`}>
      <div className={`p-3 rounded-2xl w-fit mb-3 bg-slate-50 ${color}`}>
        <Icon className="h-6 w-6" />
      </div>
      <div className="flex flex-col">
        <span className="text-xl font-extrabold text-slate-800">{count}</span>
        <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">{label}</span>
      </div>
    </Card>
  );

  if (!href) return card;

  return (
    <Link href={href} className="block rounded-[2rem] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
      {card}
    </Link>
  );
}
